import zipObject from 'lodash/zipObject';
import type { Observable as ObservableType } from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import type { CMPCustomFilter } from '@atlassian/jira-software-filters/src/common/types.tsx';
import {
	TimeFormats,
	TimeUnits,
	defaultTimeTrackingOptions,
} from '@atlassian/jira-time-tracking-formatter/src/constants.tsx';
import type { TimeFormat, TimeUnit } from '@atlassian/jira-time-tracking-formatter/src/types.tsx';
import type { Column } from '../../model/columns/types';
import { CAN_EDIT_BOARD_CONFIG } from '../../model/config/permissions/constants';
import {
	ISSUE_COUNT,
	ISSUE_COUNT_ID,
	statisticFieldPrefixes,
	statisticFieldToEstimateType,
} from '../../model/constants';
import type { BacklogStrategy, EstimationType } from '../../model/types';
import fetch, { type EditModelResponse } from '../../rest/load-edit-model';
import type { ServiceContext } from '../service-context';

export type EditModel = {
	entities: {
		estimation: {
			isEstimationFeatureEnabled: boolean;
			estimationType: EstimationType;
			estimationFieldId: string | null;
			name: string;
		};
		quickFilters: CMPCustomFilter[];
	};
	config?: {
		name?: string;
		permissions: {
			[key: string]: boolean;
		};
		backlogStrategy: BacklogStrategy;
		storyPointFieldId?: string | null;
		sprintFieldId?: string | null;
		epicIssueType: {
			id: string;
			name: string;
			iconUrl: string;
		} | null;
		epicLinkField: {
			id: string;
			name: string;
		} | null;
		timeTrackingOptions?: {
			workingHoursPerDay: number;
			workingDaysPerWeek: number;
			timeFormat: TimeFormat;
			defaultUnit: TimeUnit;
		};
		jqlQuery: string;
		isOrderedByRank: boolean;
		isBoardCrossProject: boolean;
		parallelSprints: boolean;
		showEpicAsPanel: boolean;
	};
	columns: {
		[key: string]: Column;
	};
};

interface EstimationStatistics {
	isEstimationFeatureEnabled: boolean;
	estimationType: EstimationType;
	estimationFieldId: string | null;
	name: string;
}

const transformEstimationType = (resp: EditModelResponse): EstimationType => {
	const currentStatisticFieldPrefix =
		statisticFieldPrefixes.find(
			(statisticFieldPrefix) =>
				resp?.estimationStatisticConfig?.currentEstimationStatistic?.id?.startsWith(
					statisticFieldPrefix,
				) ?? false,
		) ?? ISSUE_COUNT_ID;

	return statisticFieldToEstimateType[currentStatisticFieldPrefix] ?? ISSUE_COUNT;
};

const transformEstimation = (response: EditModelResponse): EstimationStatistics => ({
	isEstimationFeatureEnabled:
		response?.estimationStatisticConfig?.currentEstimationStatistic?.isEnabled ?? false,
	estimationType: transformEstimationType(response),
	estimationFieldId:
		response?.estimationStatisticConfig?.currentEstimationStatistic?.fieldId ?? null,
	name: response?.estimationStatisticConfig?.currentEstimationStatistic?.name ?? '',
});

export const transformer = (resp: EditModelResponse, isCompanyManaged = false): EditModel => ({
	entities: {
		estimation: transformEstimation(resp),
		quickFilters:
			resp?.quickFilterConfig?.quickFilters?.map((f) => ({ ...f, id: String(f.id) })) ?? [],
	},
	config: {
		permissions: !isCompanyManaged
			? {
					[CAN_EDIT_BOARD_CONFIG]: resp.canEdit,
				}
			: {},
		backlogStrategy: resp.backlogStrategy,
		epicIssueType: resp?.globalConfig?.epicConfig?.epicIssueTypeId
			? {
					id: resp?.globalConfig?.epicConfig?.epicIssueTypeId,
					name: resp?.globalConfig?.epicConfig?.epicIssueTypeName,
					iconUrl: resp?.globalConfig?.epicConfig?.epicIssueTypeIconUrl,
				}
			: null,
		epicLinkField: resp?.globalConfig?.epicConfig?.epicLinkFieldId
			? {
					id: resp?.globalConfig?.epicConfig?.epicLinkFieldId,
					name: resp?.globalConfig?.epicConfig?.epicLinkFieldName,
				}
			: null,
		...(isCompanyManaged
			? {
					name: resp.name,
					storyPointFieldId:
						resp?.estimationStatisticConfig?.currentEstimationStatistic?.fieldId || null,
					sprintFieldId: resp?.globalConfig?.sprintConfig?.sprintFieldId,
					timeTrackingOptions: {
						workingHoursPerDay:
							resp?.globalConfig?.timeTrackingConfig?.hoursPerDay ||
							defaultTimeTrackingOptions.workingHoursPerDay,
						workingDaysPerWeek:
							resp?.globalConfig?.timeTrackingConfig?.daysPerWeek ||
							defaultTimeTrackingOptions.workingDaysPerWeek,
						timeFormat: resp?.globalConfig?.timeTrackingConfig?.timeFormat || TimeFormats.PRETTY,
						defaultUnit:
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							(resp?.globalConfig?.timeTrackingConfig?.defaultUnit?.toLowerCase() as TimeUnit) ||
							TimeUnits.MINUTE,
					},
				}
			: undefined),
		jqlQuery: resp?.filterConfig?.query,
		isOrderedByRank: resp?.filterConfig?.isOrderedByRank,
		isBoardCrossProject: resp.isBoardCrossProject,
		parallelSprints: resp.globalConfig.parallelSprints,
		showEpicAsPanel: resp.showEpicAsPanel,
	},

	columns: zipObject(
		resp.rapidListConfig.mappedColumns.map((column) => column.id),
		resp.rapidListConfig.mappedColumns.map((column, index) => ({
			id: column.id,
			name: column.name,
			order: index,
			statuses: column.mappedStatuses.map((status) => ({
				id: status.id,
				name: status.name,
				isInitial: status.isInitial,
				isResolutionDone: status.isResolutionDone,
				category: status.statusCategory.key, // The key can only be new/undefined/done/indeterminate
			})),
		})),
	),
});

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (serviceContext: ServiceContext): ObservableType<EditModel> =>
	fetch(serviceContext.boardId).map((response) =>
		transformer(response, serviceContext.isCompanyManaged),
	);
